/**
 * Che cosa si fa qui: una riga per schermata (GI-281).
 *
 * Le schede dicono **dove** si e'; nessuna diceva **a che cosa serve** la
 * schermata. Chi arriva su «Copertura» o «Qualità» il primo giorno vede una
 * tabella e dei filtri e deve indovinare il resto dal manuale — se sa che
 * esiste.
 *
 * **Aperta la prima volta, poi un titolo con il ⓘ.** Lo stesso patto del
 * riquadro d'avvio: la riga serve la prima volta che si vede la schermata, e
 * da allora ruba spazio al lavoro. Per utenza e per schermata: chi ha capito
 * la coda non ha ancora capito la consegna.
 *
 * Una riga, non un paragrafo: dice il verbo della schermata e dove porta.
 * Il come sta nel manuale, sotto *Aiuto*; il significato delle parole sta
 * dietro il segno «?».
 */
import { useState } from 'react'
import { matchPath } from 'react-router-dom'

import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'

const PURPOSES: [string, MessageKey][] = [
  ['/uploads/new', 'purpose.upload'],
  ['/uploads/*', 'purpose.receipt'],
  ['/ingest/*', 'purpose.ingest'],
  ['/work', 'purpose.work'],
  ['/inspections/:inspectionId', 'purpose.overview'],
  ['/inspections/:inspectionId/map', 'purpose.map'],
  ['/inspections/:inspectionId/anomalies', 'purpose.anomalies'],
  ['/inspections/:inspectionId/queue', 'purpose.queue'],
  ['/inspections/:inspectionId/candidates/*', 'purpose.queue'],
  ['/inspections/:inspectionId/search', 'purpose.search'],
  ['/inspections/:inspectionId/coverage', 'purpose.coverage'],
  ['/inspections/:inspectionId/elements/*', 'purpose.element'],
  ['/inspections/:inspectionId/progress', 'purpose.progress'],
  ['/inspections/:inspectionId/quality', 'purpose.quality'],
  ['/inspections/:inspectionId/delivery', 'purpose.delivery'],
  ['/exports', 'purpose.deliveries'],
  ['/exports/*', 'purpose.deliveryReceipt'],
  ['/operations', 'purpose.operations'],
  ['/admin/users', 'purpose.users'],
  ['/admin/model', 'purpose.model'],
  ['/admin/settings', 'purpose.settings'],
  ['/admin/catalog', 'purpose.catalog'],
  ['/admin/audit', 'purpose.audit'],
]

/** La chiave della riga per questo indirizzo, o niente. */
export function purposeOf(pathname: string): MessageKey | null {
  const found = PURPOSES.find(([path]) => matchPath({ path, end: true }, pathname) !== null)
  return found === undefined ? null : found[1]
}

const SEEN = 'gridfly.purpose.seen'

function firstTime(login: string, key: MessageKey): boolean {
  try {
    return window.localStorage.getItem(`${SEEN}.${login}.${key}`) === null
  } catch {
    return true
  }
}

function markSeen(login: string, key: MessageKey): void {
  try {
    window.localStorage.setItem(`${SEEN}.${login}.${key}`, '1')
  } catch {
    // Senza memoria si riapre ogni volta: non e' un guasto.
  }
}

export function ScreenPurpose({ pathname, login }: { pathname: string; login: string }): JSX.Element | null {
  const t = useT()
  const key = purposeOf(pathname)
  const [open, setOpen] = useState<boolean>(() => (key === null ? false : firstTime(login, key)))
  if (key === null) return null
  return (
    <details
      className="scopo"
      open={open}
      onToggle={(event) => {
        const next = (event.currentTarget as HTMLDetailsElement).open
        setOpen(next)
        if (!next) markSeen(login, key)
      }}
    >
      <summary className="scopo-titolo">
        <span className="scopo-info" aria-hidden="true">
          ⓘ
        </span>
        {t('purpose.what')}
      </summary>
      <p className="scopo-testo">{t(key)}</p>
    </details>
  )
}
